import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import PointsDisclaimer from '../common/PointsDisclaimer';
import './MarketplacePage.css';

// Kosmetiksort → emoji för kort och inventory-rader.
const KIND_ICONS = { title: '🏷️', badge: '🎖️', name_color: '🎨' };

/**
 * Förhandsvisning av en kosmetik: namnfärger visas som färgprov,
 * titlar/badges som sitt värde.
 *
 * @param {{item: object}} props Katalogvaran ({kind, value, name}).
 * @returns {JSX.Element} Förhandsvisningen.
 */
function CosmeticPreview({ item }) {
  if (item.kind === 'name_color') {
    return (
      <span className="mp-preview mp-preview-color" style={{ '--swatch': item.value }}>
        {item.value}
      </span>
    );
  }
  return <span className="mp-preview">{item.value}</span>;
}

/**
 * MarketplacePage — marknaden där spelare säljer kosmetika till varandra (A3).
 *
 * Två flikar: marknaden (öppna listningar, köp) och mitt inventory (ägda
 * kosmetika: utrusta, lista till försäljning, avlista). Köp debiterar
 * plånboken; säljaren får priset minus transaktionsskatten (visas i
 * bannern). Hela sidan gateas på feature-flaggan MARKETPLACE_ENABLED —
 * routen finns bara när Johan slagit på marknaden i webbadminen.
 *
 * @returns {JSX.Element} Marketplace-sidan.
 */
export default function MarketplacePage() {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('market');
  const [state, setState] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sellCosmetic, setSellCosmetic] = useState(null);
  const [sellPrice, setSellPrice] = useState('');

  const locale = currentLocale();

  const loadMarket = useCallback(() => {
    fetch(`${API_URL}/api/marketplace`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setState(body); })
      .catch(() => {});
  }, [getAuthHeaders]);

  const loadInventory = useCallback(() => {
    if (!user) return;
    fetch(`${API_URL}/api/marketplace/inventory`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setInventory(body.cosmetics || []); })
      .catch(() => {});
  }, [user, getAuthHeaders]);

  useEffect(() => { loadMarket(); loadInventory(); }, [loadMarket, loadInventory]);

  /**
   * Kör ett POST/DELETE-anrop mot marknaden och ladda om båda vyerna.
   *
   * @param {string} path API-sökväg efter /api/marketplace.
   * @param {string} method HTTP-metod.
   * @param {object|null} body JSON-kropp.
   * @param {string} successKey i18n-nyckel för lyckat-meddelandet.
   */
  const action = async (path, method, body, successKey) => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/marketplace${path}`, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      if (!res.ok) {
        const key = {
          insufficient_balance: 'market.errInsufficient',
          marketplace_disabled: 'marketplace.closed',
          own_listing: 'marketplace.errOwnListing',
          too_many_listings: 'marketplace.errTooMany',
          locked: 'marketplace.errLocked',
        }[data.code];
        setMessage({ kind: 'error', text: key ? t(key) : (data.error || t('market.errGeneric')) });
      } else {
        setMessage({ kind: 'success', text: t(successKey) });
        window.dispatchEvent(new CustomEvent('wallet-refresh'));
        setSellCosmetic(null);
        setSellPrice('');
        loadMarket();
        loadInventory();
      }
    } catch {
      setMessage({ kind: 'error', text: t('market.errGeneric') });
    } finally {
      setBusy(false);
    }
  };

  if (!state) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  const myListingIds = new Set((state.my_listings || [])
    .filter((l) => l.status === 'open').map((l) => l.id));

  return (
    <div className="marketplace-page">
      <div className="marketplace-header">
        <h1 className="marketplace-title">🛒 {t('marketplace.title')}</h1>
        <p className="marketplace-subtitle">
          {t('marketplace.subtitle', { tax: state.tax_pct })}
        </p>
      </div>

      <PointsDisclaimer />

      {!state.enabled && (
        <div className="marketplace-closed-banner">{t('marketplace.closed')}</div>
      )}

      <div className="marketplace-tabs">
        <button
          type="button"
          className={`marketplace-tab ${tab === 'market' ? 'active' : ''}`}
          onClick={() => setTab('market')}
        >
          {t('marketplace.tabMarket')}
        </button>
        {user && (
          <button
            type="button"
            className={`marketplace-tab ${tab === 'inventory' ? 'active' : ''}`}
            onClick={() => setTab('inventory')}
          >
            {t('marketplace.tabInventory')}
          </button>
        )}
      </div>

      {message && <div className={`market-message ${message.kind}`}>{message.text}</div>}

      {tab === 'market' && (
        <div className="marketplace-listings">
          {state.listings.length === 0 ? (
            <p className="marketplace-empty">{t('marketplace.empty')}</p>
          ) : (
            state.listings.map((listing) => (
              <div key={listing.id} className="marketplace-card">
                <div className="mp-card-kind">
                  {KIND_ICONS[listing.item.kind]} {t(`marketplace.kind_${listing.item.kind}`)}
                </div>
                <div className="mp-card-name">{listing.item.name}</div>
                <CosmeticPreview item={listing.item} />
                <div className="mp-card-seller">{t('marketplace.seller', { name: listing.seller_name })}</div>
                <div className="mp-card-footer">
                  <span className="mp-card-price">🪙 {listing.price_points.toLocaleString(locale)}</span>
                  {myListingIds.has(listing.id) ? (
                    <button
                      type="button"
                      className="mp-btn mp-btn-cancel"
                      disabled={busy}
                      onClick={() => action(`/${listing.id}`, 'DELETE', null, 'marketplace.cancelled')}
                    >
                      {t('marketplace.cancelListing')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="mp-btn mp-btn-buy"
                      disabled={busy || !user || !state.enabled}
                      onClick={() => action(`/${listing.id}/buy`, 'POST', null, 'marketplace.bought')}
                    >
                      {t('marketplace.buy')}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'inventory' && user && (
        <div className="marketplace-inventory">
          {inventory.length === 0 ? (
            <p className="marketplace-empty">{t('marketplace.inventoryEmpty')}</p>
          ) : (
            inventory.map((c) => (
              <div key={c.id} className={`mp-inv-row ${c.locked ? 'locked' : ''}`}>
                <span className="mp-inv-kind">{KIND_ICONS[c.item.kind]}</span>
                <span className="mp-inv-name">{c.item.name}</span>
                <CosmeticPreview item={c.item} />
                {c.equipped && <span className="mp-inv-equipped">{t('marketplace.equipped')}</span>}
                {c.locked && <span className="mp-inv-listed">{t('marketplace.listed')}</span>}
                <div className="mp-inv-actions">
                  {!c.locked && !c.equipped && (
                    <button
                      type="button"
                      className="mp-btn mp-btn-equip"
                      disabled={busy}
                      onClick={() => action('/inventory/equip', 'POST', { player_cosmetic_id: c.id }, 'marketplace.equippedMsg')}
                    >
                      {t('marketplace.equip')}
                    </button>
                  )}
                  {c.equipped && (
                    <button
                      type="button"
                      className="mp-btn mp-btn-unequip"
                      disabled={busy}
                      onClick={() => action('/inventory/equip', 'POST', { unequip_kind: c.item.kind }, 'marketplace.unequippedMsg')}
                    >
                      {t('marketplace.unequip')}
                    </button>
                  )}
                  {!c.locked && c.item.tradeable && state.enabled && (
                    <button
                      type="button"
                      className="mp-btn mp-btn-sell"
                      disabled={busy}
                      onClick={() => { setSellCosmetic(sellCosmetic === c.id ? null : c.id); setSellPrice(''); }}
                    >
                      {t('marketplace.sell')}
                    </button>
                  )}
                </div>
                {sellCosmetic === c.id && (
                  <div className="mp-sell-form">
                    <input
                      className="market-amount-input"
                      type="number"
                      min={1}
                      placeholder={t('marketplace.pricePlaceholder')}
                      value={sellPrice}
                      onChange={(e) => setSellPrice(e.target.value)}
                    />
                    <button
                      type="button"
                      className="mp-btn mp-btn-sell"
                      disabled={busy || !sellPrice}
                      onClick={() => action('/list', 'POST', { player_cosmetic_id: c.id, price: Number(sellPrice) }, 'marketplace.listedMsg')}
                    >
                      {t('marketplace.confirmSell', { tax: state.tax_pct })}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
